import { booleanAttribute, computed, Directive, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Table } from 'primeng/table';
import { DevTableConfig, DevTableConfigValues, effectiveTablePageSize, PaginatorPosition } from '../model/table-model';

export interface HisTableMeta {
    rowsPerPage: number;
    totalRowCount: number;
    first: number;
    currentPage: number;
    currentNumberOfPages: number;
    paginatorPosition: PaginatorPosition;
    tableConfig?: DevTableConfigValues;
}

/**
 * Provides a reactive view of p-table metadata to components and directives inside the table.
 * It is attached to every `p-table` by default, unless `disableHisTableMeta` is present.
 * Descendants can inject `HisTableMetaDirective` or use the `hisTableMeta` export to read
 * its `data()` signal instead of passing and maintaining table properties manually.
 *
 * ### Metadata
 * `data()` combines table inputs with the current table position to expose the page size,
 * total row count, first row index, current page, number of pages, and paginator position.
 * When a `HisTableContextDirective` is present on the same table, its configuration is
 * also available as `tableConfig` and supplies the configured page size and paginator position.
 *
 * ### Table synchronization
 * Metadata is read-only by default. Add `enableTableMetaSync` to the `p-table` to apply
 * `rows`, `first`, and `paginatorPosition` to the underlying table when its context
 * supplies a new configuration. This avoids binding those inputs in the template.
 *
 * ### Usage example (rows-per-page)
 * ```html
 * <ng-container hisTableId="my.table">
 *   <p-table [value]="results" [paginator]="true" enableTableMetaSync>
 *     ...
 *     <ng-template pTemplate="paginatorright">
 *       <his-rows-per-page />
 *     </ng-template>
 *   </p-table>
 * </ng-container>
 * ```
 * `his-rows-per-page` reads the table metadata automatically; it does not need separate
 * inputs for the row count, current page, or page size.\
 * Its changes are automatically tracked by the table context, and in turn reflected in the table meta.
 *
 * @see HisTableContextDirective
 */
@Directive( {
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-table:not([disableHisTableMeta])',
    exportAs: 'hisTableMeta',
} )
export class HisTableMetaDirective {
    private readonly table = inject( Table, { self: true } );
    private readonly latestTableFirst = toSignal( this.table.firstChange, { initialValue: this.table.first } );

    /**
     * Enables automatic synchronization of metadata with the underlying p-table configuration.\
     * When active, the directive will automatically update these p-table inputs upon relevant changes:
     * - `rows`
     * - `first`
     * - `paginatorPosition`
     * 
     * This means they dont need to be bound in the template.
     */
    public readonly enableTableMetaSync = input( false, { transform: booleanAttribute } );

    public readonly rows = input<number>();
    public readonly value = input<any[]>();
    public readonly firstInput = input<number>( undefined, { alias: 'first' } );
    public readonly totalRecordsInput = input<number>( undefined, { alias: 'totalRecords' } );
    public readonly paginatorPositionInput = input<PaginatorPosition>( undefined, { alias: 'paginatorPosition' } );

    public readonly data = computed<HisTableMeta>( () => {
        const rowsPerPage = this.rows() ?? effectiveTablePageSize( this.tableConfig() ?? {} );
        const totalRowCount = this.totalRecordsInput() || this.value()?.length || 0;
        const first = this.parseFirst();
        const currentPage = Math.ceil( ( first + 1 ) / rowsPerPage ) || 0;
        const currentNumberOfPages = totalRowCount ? Math.ceil( totalRowCount / rowsPerPage ) : 0;
        const paginatorPosition = this.tableConfig()?.paginatorPosition || this.paginatorPositionInput() || 'bottom';

        return Object.freeze<HisTableMeta>( {
            rowsPerPage,
            totalRowCount,
            first,
            currentPage,
            currentNumberOfPages,
            paginatorPosition,
            tableConfig: this.tableConfig(),
        } );
    } );
    
    private readonly tableConfig = signal<DevTableConfig>( undefined );

    public setTableConfig( config: DevTableConfig ): void {
        this.tableConfig.set( config );
        this.updateTable( this.data() );
    }

    private updateTable( meta: HisTableMeta ): void {
        if ( !this.enableTableMetaSync() ) {
            return;
        }

        console.debug('Updating table with meta:', meta);
        this.table.rows = meta.rowsPerPage;
        this.table.first = meta.first;
        this.table.paginatorPosition = meta.paginatorPosition;
    }

    private parseFirst() {
        const changes = this.tableConfig()?.changes;
        if ( changes && ( changes.tablePageSize || changes.defaultTablePageSize ) ) {
            // if page size changes, always reset back to first page
            return 0;
        }
        return this.firstInput() || this.latestTableFirst();
    }
}
